/**
 * Created by Abner Sui on 05/21/2019.
 * Description:
 *
 * ------ maintenance history ------
 * 05/29/2019 Yoyo set default detail loading status =false, otherwise will be true forever on global search switching tab after filtering
 * Updated by Abner Sui on 08/07/2019, use threadDetailComponent, delete threadDetail2Component
 */

import { Component, OnInit, AfterViewInit, OnDestroy, Input } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store, select, createSelector } from '@ngrx/store';
import { Subject, of } from 'rxjs';
import { Subscription } from 'rxjs/Subscription';
import { take, map, switchMap, filter, catchError } from 'rxjs/operators';

import { AppState, smartSelector } from '../../redux';
import { QueryInfo, FileViewListConfig, SortInfo } from './file-view.model';
import { FileViewListActionTypes, FileViewSetSortInfo, FileViewSetKeyWord, FileViewListGetThreadSucess } from '../../redux/actions/file-view.actions';
import { KeywordSearchConfig } from '../../widgets/keyword-search/keyword-search.component';
import { getFileViewListItemsState, getKeywordState, getSortInfoState, getFileViewListMatchedAttachmentState, getActiveFileViewThreadState, getFileViewListSelectedIndex } from '../../redux/reducers/file-view.reducer';
import { StoreQuerierService } from '../../services/store-querier.service';
import { ScreenActionTypes } from '../../redux/actions/screen.actions';
import { fullEntityTypeSelector } from '../../redux/reducers/entity-type.reducer';
import { fullEntryTypeSelector } from '../../redux/reducers/entry-type.reducer';
import { lastCustomActionSelector } from '../../redux/reducers/action-history.reducer';

import { DataList } from '../../tamalelibs/models/data-list.model';
import { Thread } from '../../tamalelibs/models/thread.model';
import { DocumentEntry } from '../../tamalelibs/models/document-entry.model';
import { CREATE_NOTE_SUCCESS, DELETE_ATTACHMENT_SUCCESS, UPDATE_NOTE_SUCCESS } from '../../tamalelibs/redux/note.actions';
import { sidelistView } from '../../components/research-layout/research-layout.data';

import { FileViewConfig, FileViewConfigActionTypes, FileViewConfigFeedback, FileViewConfigFeedbackTypes } from './file-view.model';
import { ThreadDetailConfig, ThreadDetailActions } from '../../components/thread-detail/thread-detail.view-model';
import { ResearchScreenActionTypes } from '../../redux/actions/research-screen.actions';
import { ThreadService } from '../../tamalelibs/services/thread.service';
import { StringHelperService } from '../../tamalelibs/services/string-helper.service';
import { AppConfig } from '../../tamalelibs/models/app-config.model';

@Component({
    selector: 'tam-file-view',
    templateUrl: './file-view.component.html',
    styleUrls: ['./file-view.component.scss']
})
export class FileViewComponent implements OnInit, AfterViewInit, OnDestroy {
    // tslint:disable-next-line: no-input-rename
    @Input('config') config: FileViewConfig;
    isPageLoading = false;
    isThreadDetailLoading = false;
    keywordSearchConfig: KeywordSearchConfig = new KeywordSearchConfig();
    fileViewListConfig: FileViewListConfig;
    fileViewListTotalCount: number;
    sortChangeSubject: Subject<any> = new Subject();
    sortInfo: any = {
        sortBy: 'lastediteddate',
        sortOrder: 'desc',
    };
    threadDetailConfig: ThreadDetailConfig = new ThreadDetailConfig();
    sidelistView: any;
    keywordChanged = false;

    private _destroySubscriptions: Array<Subscription> = [];
    private _queryInfo: QueryInfo = new QueryInfo();
    private _selectedFileThreadId: string;
    private _actionPrefix: { name: string, value: string } = { name: null, value: null };

    constructor(
        private _store: Store<AppState>,
        private _storeQuerier: StoreQuerierService,
        private _route: ActivatedRoute,
        private _threadService: ThreadService,
    ) {
    }

    ngOnInit() {
        this.initPageOrComponent();
        this.initLayout();
        this.initComponent();
        this.isPageLoading = true;
    }

    ngAfterViewInit() {
        this.initPage();
    }

    ngOnDestroy() {
        this.destroyComponent();
    }

    initPageOrComponent() {
        if (this.config) {
            // embedded mode
            this._destroySubscriptions.push(
                this.config.actionSubject$.subscribe(action => {
                    if (action.type === FileViewConfigActionTypes.FILTER) {
                        // set searchbox
                        this.keywordSearchConfig.actions$.next({
                            type: 'SET',
                            payload: action.payload,
                        });
                    }
                }),
            );
            this._actionPrefix.name = this.config.actionKeyName;
            this._actionPrefix.value = this.config.instanceId;
        } else {
            // ngrx store is using simple reducer for this workflow, so set prefix as null
            this.config = new FileViewConfig('prefix', 'FileView');
            this._actionPrefix.name = null;
            this._actionPrefix.value = null;

            const currentPageInfo: any = {
                url: this._route.snapshot.url,
                name: 'FileView',
                type: 'FILE_VIEW'
            };
            this._store.dispatch({
                type: ScreenActionTypes.SET_VALUE,
                payload: currentPageInfo
            });
        }
    }

    initComponent() {
        // const reducerConfig: SmartReducerConfig = smartReducerConfigs.find(item => item.reducerName === FileViewConstants.ReducerName);
        // this.threadDetailConfig.keyInStore = reducerConfig.keyAliasMapper(this._actionPrefix.value);
        this.threadDetailConfig.keyInStore = this._actionPrefix.value;
        this.threadDetailConfig.dataStoreSelector = this._prepareStoreSelector(getActiveFileViewThreadState, smartSelector(this._actionPrefix.value, 'fileViewReducer'));
        this.threadDetailConfig.keywordStoreSelector = this._prepareStoreSelector(getKeywordState, smartSelector(this._actionPrefix.value, 'fileViewReducer'));
        this.threadDetailConfig.matchedAttachmentStoreSelector = this._prepareStoreSelector(getFileViewListMatchedAttachmentState, smartSelector(this._actionPrefix.value, 'fileViewReducer'));
        this.threadDetailConfig.getNoteBodyActionName = ResearchScreenActionTypes.GET_THREAD_NOTE_BODIES;

        this.fileViewListConfig = new FileViewListConfig(this._actionPrefix.name, this._actionPrefix.value);

        this._destroySubscriptions.push(
            this.keywordSearchConfig.events$.subscribe(action => this._onSubscribeKeywordSearchEvents(action)),
            this.fileViewListConfig.events$.subscribe(action => this._onSubcribeFileViewListEvents(action)),
            this._store.pipe(
                select(this._prepareStoreSelector(getFileViewListItemsState, smartSelector(this._actionPrefix.value, 'fileViewReducer')))
            ).subscribe(items => this._onSubscribeFileViewListItem(items)),
            this._store.pipe(
                select(lastCustomActionSelector),
            ).subscribe(action => this._onSubscribeActionMessage(action)),
            this._store.pipe(
                select(this._prepareStoreSelector(getFileViewListSelectedIndex, smartSelector(this._actionPrefix.value, 'fileViewReducer'))),
                filter(item => item && item.threadId),
            ).subscribe(item => this._onSelectChange(item.threadId)),
        );
    }

    initLayout() {
        this.sidelistView = Object.assign({}, sidelistView);
        this.initWidth();
        this.initMaxMin();
    }

    initWidth() {
        this.sidelistView.previewWidth = sidelistView.collapsedpreviewWidth;
        this.sidelistView.middleWidth = sidelistView.collapsedMiddleWidth;
    }

    initMaxMin() {
        this.sidelistView.previewMax = sidelistView.collapsedpreviewMax;
        this.sidelistView.previewMin = sidelistView.collapsedpreviewMin;
    }

    initPage() {
        this.isPageLoading = true;
        const constantDataReadySelector = createSelector(
            fullEntityTypeSelector,
            fullEntryTypeSelector,
            (state: AppState) => state.relationshipType,
            (allEntityTypes, allEntryTypes, allRelationshipTypes) => {
                if (allEntityTypes && allEntityTypes.length > 0 &&
                    allEntryTypes && allEntryTypes.length > 0 &&
                    allRelationshipTypes && allRelationshipTypes.length > 0) {
                    return true;
                } else {
                    return false;
                }
            }
        );
        this._store.pipe(
            select(constantDataReadySelector),
            filter(isReady => isReady),
            map(() => {
                this._initializeStore();
                this._prepareQueryInfo();
                this._triggerDataLoad();
            }),
            switchMap(() => {
                return this._store.pipe(
                    select(lastCustomActionSelector),
                    filter(action => action.type === FileViewListActionTypes.RELOAD_SUCCESS)
                );
            }),
            take(1)
        ).subscribe(() => {
            this.isPageLoading = false;
        });
    }

    destroyComponent() {
        this._destroySubscriptions.forEach(subscription => subscription.unsubscribe());
    }

    private _initializeStore() {
        this._store.dispatch(
            new FileViewSetSortInfo(
                new SortInfo(),
                this._actionPrefix.name,
                this._actionPrefix.value
            )
        );
        this._store.dispatch(
            new FileViewSetKeyWord(
                '',
                this._actionPrefix.name,
                this._actionPrefix.value
            )
        );
    }

    private _onSelectChange(threadId: string) {
        if (this._selectedFileThreadId !== threadId || this.keywordChanged) {
            if (this.keywordChanged) {
                this.keywordChanged = false;
            }
            this._selectedFileThreadId = threadId;
            this._threadService.getThreadById(this._selectedFileThreadId).pipe(
                catchError(e => {
                    return of(null);
                }),
                take(1),
            ).subscribe(data => {
                if (data) {
                    this._store.dispatch(
                        new FileViewListGetThreadSucess(
                            Thread.parse(data),
                            this._actionPrefix.name,
                            this._actionPrefix.value,
                        )
                    );
                }
            });
        }
    }

    private _onSubscribeActionMessage(action) {
        if (action.type === ResearchScreenActionTypes.GET_THREAD_NOTE_BODIES) {
            this.isThreadDetailLoading = true;
        } else if (action.type === ResearchScreenActionTypes.GET_THREAD_NOTE_BODIES_SUCCESS) {
            this.isThreadDetailLoading = false;
        } else if (action.type === CREATE_NOTE_SUCCESS || action.type === DELETE_ATTACHMENT_SUCCESS || action.type === UPDATE_NOTE_SUCCESS) { // create/update/delete note should reload
            this._triggerDataLoad();
        }
    }

    private _onSubscribeKeywordSearchEvents(action) {
        if (action.type === 'CHANGE') {
            this.keywordChanged = true;
            this.keywordSearchConfig.keyword = action.payload.state;
            this._queryInfo.searchText = action.payload.data;
            this.fileViewListConfig.actions$.next({
                type: 'RELOAD',
                payload: this._queryInfo
            });
            this._store.dispatch(
                new FileViewSetKeyWord(
                    action.payload.state,
                    this._actionPrefix.name,
                    this._actionPrefix.value
                )
            );
        }
    }

    private _onSubcribeFileViewListEvents(event) {
        if (event.type === 'SORTCHANGEED') {
            this.sortInfo = event.payload;
            this._store.dispatch(
                new FileViewSetSortInfo(
                    {
                        sortBy: event.payload.sortBy,
                        sortOrder: event.payload.sortOrder
                    },
                    this._actionPrefix.name,
                    this._actionPrefix.value
                )
            );
        } else if (event.type === 'DOUBLE_CLICK' || event.type === 'MAXIMIZE') {
            this.threadDetailConfig.actions$.next({
                type: ThreadDetailActions.TOGGLE_MAXIMIZE,
                payload: true
            });
        } else if (event.type === 'READY') {
            this.config.feedbackSubject$.next(new FileViewConfigFeedback(FileViewConfigFeedbackTypes.VIEW_READY, null));
            if (event.payload === false) {
                // list no record, set detail to unselected
                setTimeout(() => {
                    this.isThreadDetailLoading = false;
                    this.threadDetailConfig.actions$.next({
                        type: ThreadDetailActions.CLEAR,
                        payload: null
                    });
                }, AppConfig.pageTransitionDuration);
            }
        }
    }

    private _onSubscribeFileViewListItem(items: DataList<DocumentEntry>) {
        this.fileViewListTotalCount = items.totalCount ? items.totalCount : 0;
    }

    private _prepareQueryInfo() {
        this.sortInfo = this._storeQuerier.queryBySelector(this._prepareStoreSelector(getSortInfoState, smartSelector(this._actionPrefix.value, 'fileViewReducer')));
        this._queryInfo.sortBy = this.sortInfo.sortBy;
        this._queryInfo.sortOrder = this.sortInfo.sortOrder;
        if (this.config && this.config.keyword) {
            // same logic in keyword-search.component
            this._queryInfo.searchText = StringHelperService.escapeSearchString(this.config.keyword);
            this._store.dispatch(
                new FileViewSetKeyWord(
                    this.config.keyword,
                    this._actionPrefix.name,
                    this._actionPrefix.value
                )
            );
        }
    }

    private _triggerDataLoad() {
        this.fileViewListConfig.actions$.next({
            type: 'RELOAD',
            payload: this._queryInfo
        });
    }

    private _prepareStoreSelector(selector, smartSelectorParam) {
        return (state) => {
            return selector(smartSelectorParam(state));
        };
    }

}
